import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  history,
  setLocale,
  useAntdConfigSetter,
  useIntl,
  useModel,
} from '@umijs/max';
import type { MenuDataItem } from '@umijs/route-utils';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import {
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BookOutlined,
  CalendarOutlined,
  DashboardOutlined,
  FileTextOutlined,
  FormOutlined,
  GlobalOutlined,
  HomeOutlined,
  MessageOutlined,
  MonitorOutlined,
  NotificationOutlined,
  PictureOutlined,
  PieChartOutlined,
  ProfileOutlined,
  ReadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  StarOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import { canReviewEnterpriseCertification } from '@/access';
import { sessionAdapter } from '@/adapters/sessionAdapter';
import { AvatarDropdown, Footer, SelectLang, ThemeToggle } from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import { recruitmentOpsApi } from '@/modules/luole-admin/api/recruitment-ops.api';
import {
  clearAutoRefresh,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import { assembleAdminMenu, isOpsMenuItem } from '@/utils/adminMenu';
import defaultSettings from '../config/defaultSettings';
import { isOpsMenuItemAllowed } from './access';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

type MenuGroupConfig = {
  name: string;
  icon: string;
  itemNames: string[];
  children?: MenuGroupConfig[];
  hideInMenu?: boolean;
};

const menuGroups: MenuGroupConfig[] = [
  {
    name: 'luole-admin.ops',
    icon: 'audit',
    itemNames: ['luole-admin.ops.overview'],
    children: [
      {
        name: 'luole-admin.ops.jobs',
        icon: 'appstore',
        itemNames: [
          'luole-admin.ops.jobs.all',
          'luole-admin.ops.jobs.followed',
          'luole-admin.ops.jobs.viewed',
          'luole-admin.ops.jobs.favorites',
        ],
      },
      {
        name: 'luole-admin.ops.enterprises',
        icon: 'bank',
        itemNames: [
          'luole-admin.ops.enterprises.mine',
          'luole-admin.ops.enterprises.team',
          'luole-admin.ops.enterprises.all',
        ],
      },
      {
        name: 'luole-admin.ops.talents',
        icon: 'user',
        itemNames: [
          'luole-admin.ops.talents.favorites',
          'luole-admin.ops.talents.followed',
          'luole-admin.ops.talents.viewed',
        ],
      },
    ],
  },
  {
    name: 'luole-admin.architect-talent-ops',
    icon: 'apartment',
    itemNames: [
      'luole-admin.architect-talent-ops.overview',
      'luole-admin.member.management',
      'luole-admin.member.analytics',
      'luole-admin.inviteManagement',
      'luole-admin.schoolShowcase',
      'luole-admin.carousel.management',
      'luole-admin.contentMarketing.management',
      'luole-admin.resume.templates',
    ],
  },
  {
    name: 'luole-admin.recruitment-assistant-ops',
    icon: 'robot',
    itemNames: [
      'luole-admin.recruitment-assistant-ops.jobs',
      'luole-admin.enterprise.list',
      'luole-admin.enterprise.users',
      'luole-admin.enterprise.verify',
      'luole-admin.demoReservation',
      'luole-admin.userFeedback',
      'luole-admin.forumRegistration',
      'luole-admin.jobFairRegistration',
      'luole-admin.global.dictionary',
      'luole-admin.registrationChannels',
    ],
  },
  {
    name: 'luole-admin.campus-ops',
    icon: 'apartment',
    itemNames: ['luole-admin.institution.verify'],
  },
  {
    name: 'luole-admin.recruitment',
    icon: 'barChart',
    hideInMenu: true,
    itemNames: [
      'luole-admin.analytics.recruitment',
      'luole-admin.task.management',
    ],
  },
  {
    name: 'luole-admin.activity',
    icon: 'calendar',
    itemNames: ['luole-admin.job-fair.management'],
  },
  {
    name: 'luole-admin.system',
    icon: 'setting',
    itemNames: [
      'luole-admin.system.role-permissions',
      'luole-admin.system.members',
    ],
  },
];

const MENU_GROUP_PATH: Record<string, string> = {
  'luole-admin.ops': '/ops',
  'luole-admin.ops.enterprises': '/ops/enterprises',
  'luole-admin.ops.jobs': '/ops/jobs',
  'luole-admin.ops.talents': '/ops/talents',
  'luole-admin.architect-talent-ops': '/architect-talent-ops',
  'luole-admin.recruitment-assistant-ops': '/recruitment-assistant-ops',
  'luole-admin.campus-ops': '/campus-ops',
  'luole-admin.recruitment': '/task-management',
  'luole-admin.activity': '/job-fair',
  'luole-admin.system': '/system',
};

const TOP_MENU_MATCHERS: Array<{ group: string; prefixes: string[] }> = [
  { group: 'luole-admin.ops', prefixes: ['/ops'] },
  {
    group: 'luole-admin.architect-talent-ops',
    prefixes: ['/architect-talent-ops'],
  },
  {
    group: 'luole-admin.recruitment-assistant-ops',
    prefixes: ['/recruitment-assistant-ops'],
  },
  { group: 'luole-admin.campus-ops', prefixes: ['/campus-ops'] },
  {
    group: 'luole-admin.recruitment',
    prefixes: ['/task-management', '/analytics/recruitment'],
  },
  { group: 'luole-admin.activity', prefixes: ['/job-fair'] },
  { group: 'luole-admin.system', prefixes: ['/system'] },
];

const matchPathPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const getSelectedMenuKeys = (pathname: string) => {
  const keys = [pathname];
  if (matchPathPrefix(pathname, '/dashboard')) {
    keys.push('/dashboard');
  }
  const hit = TOP_MENU_MATCHERS.find(({ prefixes }) =>
    prefixes.some((prefix) => matchPathPrefix(pathname, prefix)),
  );
  if (hit) {
    keys.push(`group:${hit.group}`);
    const groupPath = MENU_GROUP_PATH[hit.group];
    if (groupPath) keys.push(groupPath);
  }
  return Array.from(new Set(keys));
};

const MENU_ICON_MAP: Record<string, React.ComponentType> = {
  apartment: ApartmentOutlined,
  appstore: AppstoreOutlined,
  audit: AuditOutlined,
  bank: BankOutlined,
  barChart: BarChartOutlined,
  book: BookOutlined,
  calendar: CalendarOutlined,
  dashboard: DashboardOutlined,
  fileText: FileTextOutlined,
  form: FormOutlined,
  global: GlobalOutlined,
  message: MessageOutlined,
  monitor: MonitorOutlined,
  notification: NotificationOutlined,
  picture: PictureOutlined,
  pieChart: PieChartOutlined,
  profile: ProfileOutlined,
  read: ReadOutlined,
  robot: RobotOutlined,
  safetyCertificate: SafetyCertificateOutlined,
  schedule: ScheduleOutlined,
  setting: SettingOutlined,
  shareAlt: ShareAltOutlined,
  solution: SolutionOutlined,
  star: StarOutlined,
  team: TeamOutlined,
  user: UserOutlined,
  userAdd: UserAddOutlined,
};

const resolveMenuIcon = (icon?: React.ReactNode) => {
  if (!icon || typeof icon !== 'string') return icon;
  const Comp =
    MENU_ICON_MAP[icon] ||
    MENU_ICON_MAP[icon.charAt(0).toLowerCase() + icon.slice(1)];
  return Comp ? <Comp /> : undefined;
};

const MENU_LOCALE_NAME: Record<string, string> = {
  'luole-admin.ops': '招聘工作台',
  'luole-admin.ops.workbench': '全部岗位',
  'luole-admin.ops.jobs': '岗位',
  'luole-admin.ops.jobs.all': '全部岗位',
  'luole-admin.ops.jobs.followed': '我跟进过的',
  'luole-admin.ops.jobs.viewed': '我浏览过的',
  'luole-admin.ops.jobs.favorites': '我收藏的',
  'luole-admin.ops.overview': '招聘总览',
  'luole-admin.ops.enterprises': '企业',
  'luole-admin.ops.enterprises.mine': '我负责的',
  'luole-admin.ops.enterprises.team': '按部门分配',
  'luole-admin.ops.enterprises.unassigned': '未分配',
  'luole-admin.ops.enterprises.all': '全部企业',
  'luole-admin.ops.talents': '人才跟进',
  'luole-admin.ops.talents.favorites': '我的收藏',
  'luole-admin.ops.talents.followed': '我跟进过的',
  'luole-admin.ops.talents.viewed': '我浏览过的',
  'luole-admin.ops.members': '角色权限',
  'luole-admin.dashboard': '首页',
  'luole-admin.architect-talent-ops': '人才端',
  'luole-admin.architect-talent-ops.overview': '运营总览',
  'luole-admin.member.management': '人才库',
  'luole-admin.member.analytics': '人才画像',
  'luole-admin.inviteManagement': '邀请管理',
  'luole-admin.schoolShowcase': '高校风采',
  'luole-admin.carousel.management': '轮播图',
  'luole-admin.contentMarketing.management': '内容营销',
  'luole-admin.resume.templates': '简历模版',
  'luole-admin.recruitment-assistant-ops': '企业端',
  'luole-admin.recruitment-assistant-ops.jobs': '职位管理',
  'luole-admin.enterprise.list': '企业库',
  'luole-admin.enterprise.users': '企业用户',
  'luole-admin.enterprise.verify': '企业认证',
  'luole-admin.demoReservation': '预约演示',
  'luole-admin.userFeedback': '用户反馈',
  'luole-admin.forumRegistration': '论坛报名',
  'luole-admin.jobFairRegistration': '招聘会报名',
  'luole-admin.global.dictionary': '数据字典',
  'luole-admin.registrationChannels': '注册渠道',
  'luole-admin.campus-ops': '高校端',
  'luole-admin.institution.verify': '机构认证',
  'luole-admin.recruitment': '招聘会',
  'luole-admin.analytics.recruitment': '招聘分析',
  'luole-admin.task.management': '任务管理',
  'luole-admin.activity': '任务管理',
  'luole-admin.job-fair.management': '招聘会管理',
  'luole-admin.system': '系统设置',
  'luole-admin.system.role-permissions': '角色权限',
  'luole-admin.system.members': '成员管理',
  '/recruitment-assistant-ops/jobs': '职位管理',
  '/ops/workbench': '全部岗位',
  '/ops/jobs': '岗位',
  '/ops/jobs/all': '全部岗位',
  '/ops/jobs/followed': '我跟进过的',
  '/ops/jobs/viewed': '我浏览过的',
  '/ops/jobs/favorites': '我收藏的',
  '/ops/overview': '招聘总览',
  '/ops/enterprises': '企业',
  '/ops/enterprises/mine': '我负责的',
  '/ops/enterprises/team': '按部门分配',
  '/ops/enterprises/unassigned': '未分配',
  '/ops/enterprises/all': '全部企业',
  '/ops/talents': '人才跟进',
  '/ops/talents/favorites': '我的收藏',
  '/ops/talents/followed': '我跟进过的',
  '/ops/talents/viewed': '我浏览过的',
};

const resolveMenuName = (item: MenuDataItem): string => {
  const key = String(item.name || '').replace(/^menu\./, '');
  if (key && !key.includes('.') && !key.startsWith('luole-admin')) {
    return key;
  }
  if (MENU_LOCALE_NAME[key]) return MENU_LOCALE_NAME[key];
  const suffix = Object.keys(MENU_LOCALE_NAME)
    .filter((alias) => alias.startsWith('luole-admin.'))
    .sort((a, b) => b.length - a.length)
    .find((alias) => key === alias || key.endsWith(`.${alias}`));
  if (suffix) return MENU_LOCALE_NAME[suffix];
  return key.split('.').pop() || key;
};

const localizeMenuItem = (item: MenuDataItem): MenuDataItem => ({
  ...item,
  name: resolveMenuName(item),
  icon: resolveMenuIcon(item.icon),
  locale: false,
  children: Array.isArray(item.children)
    ? item.children.map(localizeMenuItem)
    : item.children,
});

const flattenMenuItems = (items: MenuDataItem[] = []): MenuDataItem[] =>
  items.flatMap((item) => [
    item,
    ...flattenMenuItems((item.children as MenuDataItem[]) || []),
  ]);

const hasVisibleMenuChildren = (item: MenuDataItem) =>
  Array.isArray(item.children) && item.children.length > 0;

const groupMenuData = (
  menuData: MenuDataItem[] = [],
  options?: {
    canOps?: boolean;
    canOpsAdmin?: boolean;
    canEnterpriseVerify?: boolean;
    permissions?: string[];
  },
) => {
  const usedNames = new Set<string>();
  const menuDataMap = new Map(
    flattenMenuItems(menuData)
      .filter((item) => !!item.name)
      .map((item) => [item.name as string, item]),
  );

  const buildGroup = (group: MenuGroupConfig): MenuDataItem | null => {
    const children = [
      ...group.itemNames
        .map((name) => {
          const existing = menuDataMap.get(name);
          if (existing) return existing;
          if (name === 'luole-admin.system.members') {
            return {
              name,
              path: '/system/members',
              icon: 'team',
            };
          }
          if (name === 'luole-admin.system.role-permissions') {
            return {
              name,
              path: '/system/role-permissions',
              icon: 'safetyCertificate',
            };
          }
          return undefined;
        })
        .filter(Boolean),
      ...(group.children ?? []).map(buildGroup).filter(Boolean),
    ].filter((item) => {
      if (!item?.name) return false;
      if (hasVisibleMenuChildren(item)) return true;
      return isOpsMenuItemAllowed(item.name, {
        permissions: options?.permissions,
        canOpsAdmin: options?.canOpsAdmin,
        canEnterpriseVerify: options?.canEnterpriseVerify,
      });
    }) as MenuDataItem[];

    if (!children.length) {
      return null;
    }

    children.forEach((item) => {
      if (item.name) {
        usedNames.add(item.name);
      }
    });
    usedNames.add(group.name);

    const firstChildPath = children.find(
      (child) => child.path && !String(child.path).startsWith('/__menu/'),
    )?.path;
    return localizeMenuItem({
      name: group.name,
      icon: group.icon,
      key: `group:${group.name}`,
      path: MENU_GROUP_PATH[group.name] || firstChildPath || '/dashboard',
      hideInMenu: group.hideInMenu,
      hideInBreadcrumb: true,
      children: children.map((child) => ({
        ...child,
        parentKeys: [`group:${group.name}`],
      })),
    });
  };

  const consumeGroupNames = (group: MenuGroupConfig) => {
    usedNames.add(group.name);
    for (const name of group.itemNames) {
      usedNames.add(name);
    }
    for (const child of group.children ?? []) {
      consumeGroupNames(child);
    }
  };
  menuGroups.forEach(consumeGroupNames);

  const groupedMenu = menuGroups
    .map((group) => {
      if (group.hideInMenu) return null;
      if (group.name === 'luole-admin.ops' && options?.canOps === false) {
        return null;
      }
      return buildGroup(group);
    })
    .filter(Boolean) as MenuDataItem[];

  const permissionOptions = {
    permissions: options?.permissions,
    canOpsAdmin: options?.canOpsAdmin,
    canEnterpriseVerify: options?.canEnterpriseVerify,
  };
  const filterByPermission = (item: MenuDataItem): MenuDataItem | null => {
    if (item.children?.length) {
      const children = item.children
        .map(filterByPermission)
        .filter(Boolean) as MenuDataItem[];
      if (!children.length) return null;
      return { ...item, children };
    }
    if (!item.name) return item;
    return isOpsMenuItemAllowed(item.name, permissionOptions) ? item : null;
  };
  const remainingMenu = menuData
    .filter((item) => !item.name || !usedNames.has(item.name))
    .filter((item) => !isOpsMenuItem(item) || !groupedMenu.some(isOpsMenuItem))
    .map(filterByPermission)
    .filter(Boolean)
    .map(localizeMenuItem) as MenuDataItem[];

  return assembleAdminMenu(remainingMenu, groupedMenu);
};

const NARROW_SIDER_WIDTH = 180;

const getSettingsWithSiderWidth = (locale?: string) => {
  const stored = getStoredSiderWidth();
  const siderWidth =
    !stored || stored === DEFAULT_SIDER_WIDTH ? NARROW_SIDER_WIDTH : stored;
  return {
    ...defaultSettings,
    title: getBrandAppName(locale),
    siderWidth,
  };
};

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle, workbenchUrl }) => {
  const intl = useIntl();
  const workbenchLabel = intl.formatMessage({ id: 'menu.account.workbench' });

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
      <button
        type="button"
        className="app-header-workbench-btn"
        onClick={() => {
          if (!workbenchUrl) {
            return;
          }
          window.open(workbenchUrl, '_self');
        }}
        disabled={!workbenchUrl}
        title={workbenchLabel}
      >
        <HomeOutlined />
        <span className="app-header-workbench-text">{workbenchLabel}</span>
      </button>
    </div>
  );
};

const SessionSync: React.FC = () => {
  const { setInitialState } = useModel('@@initialState');
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (typeof setInitialState !== 'function') {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      void recruitmentOpsApi
        .getMe()
        .then((opsMe) => {
          setInitialState((prev: any) => ({ ...(prev || {}), opsMe }));
        })
        .catch(() => {
          setInitialState((prev: any) => ({ ...(prev || {}), opsMe: null }));
        });
      return;
    }

    clearAutoRefresh();
    setInitialState((prev: any) => ({ ...(prev || {}), opsMe: null }));
  }, [currentUser, loading, setInitialState]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export function innerProvider(container: React.ReactNode) {
  return <RootSessionContainer>{container}</RootSessionContainer>;
}

const RootSessionContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <SessionSync />
      {children}
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  loading?: boolean;
}> {
  return {
    loading: true,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    defaultOpenAll: true,
    menu: {
      ...((initialState?.settings as { menu?: Record<string, unknown> })
        ?.menu || {}),
      defaultOpenAll: true,
      autoClose: false,
      ignoreFlatMenu: true,
    },
    menuProps: {
      multiple: false,
      selectedKeys: getSelectedMenuKeys(history.location.pathname),
    },
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    menuDataRender: (menuData) => {
      const currentUser = initialState?.currentUser as
        | { isAdmin?: boolean; type?: string }
        | undefined;
      const opsMe = (
        initialState as
          | {
              opsMe?: {
                role?: string;
                permissions?: string[];
                canEnterpriseVerify?: boolean;
              } | null;
            }
          | undefined
      )?.opsMe;
      const opsRolePending = opsMe === undefined;
      const isPlatformAdmin = Boolean(
        currentUser?.isAdmin || currentUser?.type === 'admin',
      );
      const canOpsAdmin =
        opsMe?.role === 'admin' || (opsRolePending && isPlatformAdmin);
      const canEnterpriseVerify =
        canReviewEnterpriseCertification(opsMe) ||
        (opsRolePending && isPlatformAdmin);
      const canOps =
        Boolean(currentUser) || Boolean(opsMe?.role) || canOpsAdmin;
      return groupMenuData(menuData, {
        canOps,
        canOpsAdmin,
        canEnterpriseVerify,
        permissions: opsMe?.permissions,
      });
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (
        !showFooter ||
        history.location.pathname.includes('/member/management')
      ) {
        return false;
      }
      return <Footer />;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={appTitle}
        workbenchUrl={workbenchUrl}
      />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          {children}
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
